import { PageHeader } from "@shared/components/PageHeader";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { toast } from "@shared/ui/use-toast";
import {
  FileText,
  Mail,
  MessageSquare,
  Clock,
} from "lucide-react";
import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ContactOverviewDashboard from "./contactOverviewDashboard";
import { useGetWorkflowDetails } from "@features/ux/components/workflow/hooks";

interface ReviewInfoStepProps {
  editMode?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  globalFormData?: any;
  updateGlobalFormData?: (data: any) => void;
}

export const ReviewInfoStep: React.FC<ReviewInfoStepProps> = ({
  editMode = false,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  globalFormData,
  updateGlobalFormData,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const { data: workflowDetail } = useGetWorkflowDetails(Number(id));

  // Workflow data from API
  const workflowData = {
    workflowName: workflowDetail?.result?.name || "N/A",
    description: workflowDetail?.result?.description || "N/A",
    actionType: workflowDetail?.result?.actionTypeDisplay || "N/A",
    audienceType: workflowDetail?.result?.audienceTypeDisplay || "N/A",
    recipientList: workflowDetail?.result?.recipientListDisplay || "N/A",
    totalRecipients: workflowDetail?.result?.recipients || 0,
    triggerEvent: workflowDetail?.result?.triggerTypeDisplay,
    scheduleType: workflowDetail?.result?.scheduleTypeDisplay,
    scheduleDate: workflowDetail?.result?.scheduledate,
    scheduleTime: workflowDetail?.result?.scheduleTime,
    frequency: workflowDetail?.result?.frequencyDisplay,
    startDate: workflowDetail?.result?.startDate,
    endDate: workflowDetail?.result?.endDate,
    time: workflowDetail?.result?.scheduleTime,
  };

  const contactStats = {
    totalUsers: 45,
    emailOnly: 13,
    phoneOnly: 9,
    both: 22,
    noContact: 2,
  };

  // Check if sequencing is enabled
  const enableSequencing = workflowDetail?.result?.enableSequencing || false;

  // Get message sequence data
  const messageSequence = useMemo(() => {
    if (enableSequencing && workflowDetail?.result?.messageSequence) {
      return workflowDetail.result.messageSequence
        .sort((a: any, b: any) => a.sequenceOrder - b.sequenceOrder)
        .map((item: any) => ({
          channel: item.channelName?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channelName,
          templateName: item.templateName,
          waitDuration: item.waitDuration,
          waitUnit: item.waitUnit,
          sequenceOrder: item.sequenceOrder,
        }));
    }
    return [];
  }, [workflowDetail, enableSequencing]);

  // Get communication channels for non-sequencing mode
  const communicationChannels = useMemo(() => {
    if (!enableSequencing) {
      // Check if templates array exists
      if (workflowDetail?.result?.templates && workflowDetail.result.templates.length > 0) {
        return workflowDetail.result.templates.map((item: any) => ({
          channel: item.channelDisplay?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channelDisplay || item.channel_display,
          templateName: item.templateName || item.template_name,
        }));
      }
      
      // Fallback to communication_channels array
      return (
        workflowDetail?.result?.communicationChannels?.map((item: any) => ({
          channel: item.channel?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channel,
          templateName: item.templateName,
        })) || []
      );
    }
    return [];
  }, [workflowDetail, enableSequencing]);

  // Group channels by type for non-sequencing mode
  const groupedChannels = useMemo(() => {
    return communicationChannels.reduce((acc, channel) => {
      if (!acc[channel.channel]) {
        acc[channel.channel] = [];
      }
      acc[channel.channel].push(channel);
      return acc;
    }, {} as Record<string, typeof communicationChannels>);
  }, [communicationChannels]);

  // Show contact overview only if recipient list is selected
  const showContactOverview = !!workflowData.recipientList;

  const getChannelIcon = (channelType: string) => {
    if (channelType === "email") {
      return <Mail className="h-5 w-5 text-blue-600" />;
    }
    return <MessageSquare className="h-5 w-5 text-green-600" />;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Build the complete workflow payload
      const workflowPayload = {
        ...globalFormData,
        workflowData,
        contactStats,
        communicationChannels: enableSequencing
          ? messageSequence
          : communicationChannels,
      };

      // Replace with actual API call
      // const response = await saveWorkflow(workflowPayload);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Success!",
        description: "Workflow saved successfully.",
      });

      // Navigate to workflows list
      navigate("/communication-hub/workflows");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save workflow. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white p-1">
      <div>
        <PageHeader
          title="Review Your Workflow"
          subtitle="Please review all details before saving"
        />
      </div>

      {/* Contact Overview Dashboard - Only shown if recipient list is selected */}
      {showContactOverview && (
        <ContactOverviewDashboard data={workflowDetail} />
      )}

      {/* Workflow Details Section */}
      <div className="space-y-6">
        {/* Merged Workflow Details Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-base font-semibold text-gray-900">
                Workflow Details
              </h3>
            </div>
          </div>
          <div className="p-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-4">
              {/* Basic Workflow Info */}
              <ValueLabel
                label="Workflow Name"
                value={workflowData.workflowName}
                tooltip="The unique name of this workflow"
              />
              <ValueLabel
                label="Action Type"
                value={workflowData.actionType}
                tooltip="When the workflow will be executed (Immediate or Time-based)"
              />
              <ValueLabel
                label="Description"
                value={workflowData.description}
                tooltip="Brief description of the workflow purpose"
              />

              {/* Audience Info */}
              <ValueLabel
                label="Audience Type"
                value={workflowData.audienceType}
                tooltip="The type of audience targeted by this workflow"
              />
              {workflowData.recipientList && (
                <>
                  <ValueLabel
                    label="Recipient List"
                    value={workflowData.recipientList}
                    tooltip="The selected recipient list for this workflow"
                  />
                  <ValueLabel
                    label="Total Recipients"
                    value={workflowData.totalRecipients?.toString() || "N/A"}
                    tooltip="Total number of recipients in the selected list"
                  />
                </>
              )}

              {/* Schedule Info */}
              {workflowData.triggerEvent && (
                <ValueLabel
                  label="Trigger Event"
                  value={workflowData.triggerEvent}
                  tooltip="The event that will trigger this workflow"
                />
              )}
              {workflowData.scheduleType === "Recurring" && (
                <>
                  {workflowData.frequency && (
                    <ValueLabel
                      label="Frequency"
                      value={workflowData.frequency}
                      tooltip="How often this workflow repeats"
                    />
                  )}
                  {workflowData.scheduleDate && (
                    <ValueLabel
                      label="Schedule Date"
                      value={workflowData.scheduleDate}
                      tooltip="The date when this workflow will run"
                    />
                  )}
                  {workflowData.scheduleTime && (
                    <ValueLabel
                      label="Schedule Time"
                      value={workflowData.scheduleTime}
                      tooltip="The time when this workflow will run"
                    />
                  )}
                  {workflowData.startDate && (
                    <ValueLabel
                      label="Start Date"
                      value={workflowData.startDate}
                      tooltip="The date when this workflow schedule begins"
                    />
                  )}
                  {workflowData.endDate && (
                    <ValueLabel
                      label="End Date"
                      value={workflowData.endDate}
                      tooltip="The date when this workflow schedule ends"
                    />
                  )}
                  {workflowData.time && workflowData.time !== workflowData.scheduleTime && (
                    <ValueLabel
                      label="Time"
                      value={workflowData.time}
                      tooltip="The time of day for workflow execution"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Communication Channels Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h3 className="text-base font-semibold text-gray-900">
                  Communication Channels
                </h3>
              </div>
              {enableSequencing && messageSequence.length > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    Sequence Enabled
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {messageSequence.length} steps configured
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 bg-white">
            {enableSequencing ? (
              // Sequencing Mode UI
              <div className="space-y-0">
                {messageSequence.map((item, index) => (
                  <div key={index} className="relative">
                    {/* Step Card */}
                    <div className="flex items-start gap-4">
                      {/* Channel Icon */}
                      <div
                        className={`${
                          item.channel === "email"
                            ? "bg-blue-100"
                            : "bg-green-100"
                        } rounded-lg p-3 flex-shrink-0`}
                      >
                        {getChannelIcon(item.channel)}
                      </div>

                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {item.channelName} Message
                          </span>
                          <Badge variant="outline" className="text-xs">
                            Step {item.sequenceOrder}
                          </Badge>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <p className="text-sm text-gray-900 font-medium">
                            {item.templateName}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Wait Duration - Only show between steps */}
                    {index < messageSequence.length - 1 && (
                      <div className="flex items-center gap-4 py-3 pl-3">
                        {/* Vertical Line */}
                        <div className="w-12 flex justify-center">
                          <div className="h-8 w-0.5 bg-gray-300"></div>
                        </div>
                        {/* Wait Info */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>
                            Wait {item.waitDuration || 0} {item.waitUnit || "minutes"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Non-Sequencing Mode UI (2-column grid)
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(groupedChannels).map(
                  ([channelType, channels]) => {
                    const channelList = channels as Array<{
                      channel: "email" | "sms";
                      channelName: string;
                      templateName: string;
                    }>;

                    return (
                      <div
                        key={channelType}
                        className="border border-gray-200 rounded-lg p-4 bg-blue-50"
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className={`${
                              channelType === "email"
                                ? "bg-blue-500"
                                : "bg-green-500"
                            } rounded-lg p-3 flex-shrink-0`}
                          >
                            {channelType === "email" ? (
                              <Mail className="h-5 w-5 text-white" />
                            ) : (
                              <MessageSquare className="h-5 w-5 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900 mb-1">
                              {channelType === "email"
                                ? "Email Channel"
                                : "Text Message Channel"}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                              {channelList.length} template
                              {channelList.length > 1 ? "s" : ""} selected
                            </p>
                            <ul className="space-y-2">
                              {channelList.map((channel, index) => (
                                <li
                                  key={index}
                                  className="flex items-center gap-2 text-sm text-gray-900"
                                >
                                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                                  <span className="font-medium">
                                    {channel.templateName}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={onPrevious} disabled={isFirstStep} variant="outline">
          Previous
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              Saving...
            </>
          ) : (
            "Save Workflow"
          )}
        </Button>
      </div>
    </div>
  );
};